/**
 * Left rail — fixed 264px, gradient, three regions: lockup / nav / footer.
 *
 * Gating reads the tenant's cap_* booleans, not tier. The schema comment states
 * tier sets defaults but individual flags override, so a Base tenant may
 * legitimately have a Pro capability switched on and must not see it locked.
 */
import { c, radius, rail, railGradient, mono } from '../theme/tokens';
import { Dot } from './primitives';
import type { DashboardContext } from '../api/types';
import type { Screen, UpgradeFocus, UpgradeTier } from '../types';
import { Lockup, ModelPropSignoff } from './Lockup';

/** The ModelProp console (every Prop product an agency has). Set VITE_MODELPROP_CONSOLE_URL to move it. */
const MODELPROP_CONSOLE_URL =
  (import.meta.env.VITE_MODELPROP_CONSOLE_URL as string | undefined) || 'https://modelprop-console.vercel.app';

interface NavItem {
  name: string;
  screen: Screen | null;
  count: string;
  upgradeFocus?: UpgradeFocus;
  status?: string;
  disabled?: boolean;
}

interface NavGroup {
  label: string;
  tier?: 'PRO' | 'SELECT';
  /** The upgrade drawer to open. The Base roadmap is intentionally Select-led. */
  upgradeTier?: Exclude<UpgradeTier, null>;
  locked: boolean;
  items: NavItem[];
}

export function Rail({
  ctx,
  screen,
  callCount,
  followUpCount,
  onNavigate,
  onLocked,
}: {
  ctx: DashboardContext;
  screen: Screen;
  callCount: number | null;
  followUpCount: number | null;
  onNavigate: (s: Screen) => void;
  onLocked: (tier: 'pro' | 'select', focus?: UpgradeFocus) => void;
}) {
  const caps = ctx.capabilities;

  // Pro group needs both Pro capabilities; Select needs the Select trio.
  const proUnlocked = caps.cap_property_search && caps.cap_crm_booking;
  const selectUnlocked = caps.cap_staff_routing && caps.cap_document_access && caps.cap_omniprop_intel;

  const baseMessageOnly = !caps.cap_review_queue;
  const isProCustomer = ctx.tenant.tier === 'pro' || ctx.tenant.tier === 'select';
  // Shaun has Pro access waiting on his Street CRM link, not an unavailable upgrade.
  const shaunAwaitingStreetLink = ctx.tenant.slug === 'shaunmellor';
  const groups: NavGroup[] = baseMessageOnly ? [
    { label: 'TODAY', locked: false, items: [
      { name: 'Action Centre', screen: 'overview', count: '' },
      { name: 'Call Record', screen: 'calls', count: callCount == null ? '' : String(callCount) },
    ] },
    { label: 'UNDERSTAND', locked: false, items: [
      { name: 'Insights', screen: 'themes', count: '' },
    ] },
    {
      label: 'ADVISE',
      tier: 'PRO',
      upgradeTier: 'pro',
      locked: !isProCustomer,
      items: isProCustomer ? [
        { name: 'Property Conversations', screen: null, count: '', status: 'waiting', disabled: true },
        { name: 'AI Voice Choice', screen: 'studio', count: '' },
      ] : [
        { name: 'Property Conversations', screen: null, count: '', upgradeFocus: 'property-conversations' },
        { name: 'AI Voice Choice', screen: null, count: '', upgradeFocus: 'area-conversations' },
      ],
    },
    {
      label: 'SELECT',
      tier: 'SELECT',
      locked: true,
      items: [
        { name: 'Viewings Requested', screen: null, count: '', upgradeFocus: 'viewings-requested' },
        { name: 'Viewings Booked', screen: null, count: '', upgradeFocus: 'viewings-booked' },
      ],
    },
    { label: 'ACCOUNT', locked: false, items: [
      ...(ctx.actor?.canManageTeam ? [{ name: 'Team access', screen: 'team' as Screen, count: '' }] : []),
    ] },
  ] : [
    { label: 'TODAY', locked: false, items: [{ name: 'Overview', screen: 'overview', count: '' }] },
    {
      label: 'CALLS',
      locked: false,
      items: [
        { name: 'Call record', screen: 'calls', count: callCount == null ? '' : String(callCount) },
        { name: 'Action centre', screen: 'overview', count: followUpCount == null ? '' : String(followUpCount) },
      ],
    },
    { label: 'UNDERSTAND', locked: false, items: [{ name: 'Themes & sentiment', screen: 'themes', count: '' }] },
    {
      label: 'CONVERT',
      tier: 'PRO',
      locked: !proUnlocked,
      items: [
        { name: 'Viewings', screen: null, count: '' },
        { name: 'Vendor leads', screen: null, count: '' },
      ],
    },
    {
      label: 'KNOWLEDGE',
      tier: 'SELECT',
      locked: !selectUnlocked,
      items: [
        { name: 'Knowledge Centre', screen: null, count: '' },
        { name: 'Routing & people', screen: null, count: '' },
      ],
    },
    {
      label: 'STUDIO',
      locked: false,
      items: [{ name: 'Voice Studio', screen: 'studio', count: 'new' }],
    },
    {
      label: 'ADMIN',
      locked: false,
      items: [
        // Team is shown only to owners and admins. This is presentation: the
        // /team routes re-check the role server-side regardless.
        ...(ctx.actor?.canManageTeam ? [{ name: 'Team access', screen: 'team' as Screen, count: '' }] : []),
        { name: 'Assistant settings', screen: null, count: '' },
        { name: 'Numbers & branches', screen: null, count: '' },
      ],
    },
  ];

  const branchLabel = ctx.branch.name.toUpperCase();
  const userInitials = ctx.tenant.name
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <aside
      className="vp-rail"
      style={{
        width: 264,
        flex: '0 0 264px',
        background: railGradient,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Lockup */}
      <div style={{ padding: '26px 22px 22px 22px', borderBottom: `1px solid ${rail.divider}` }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Lockup onDark />
          <div style={{ fontFamily: mono, fontSize: 8.5, letterSpacing: '0.20em', color: c.sky, paddingLeft: 38 }}>BY MODELPROP</div>
        </div>
      </div>

      {/* Nav */}
      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '18px 12px 12px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {groups.map(group => (
          <div key={group.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 9px 12px' }}>
              <span style={{ fontFamily: mono, fontSize: 9.5, letterSpacing: '0.16em', color: rail.sectionLabel }}>
                {group.label}
              </span>
              {group.tier ? (
                <span
                  style={{
                    fontFamily: mono,
                    fontSize: 9,
                    letterSpacing: '0.10em',
                    color: c.mustard,
                    border: '1px solid rgba(255,221,74,0.35)',
                    borderRadius: radius.pill,
                    padding: '1px 6px 2px 6px',
                  }}
                >
                  {group.tier}
                </span>
              ) : null}
            </div>

            {group.items.map(item => {
              const locked = group.locked || Boolean(item.disabled);
              const active = !locked && (item.screen === screen || (item.screen === 'calls' && screen === 'detail'));
              const gated = group.locked && !!group.tier;

              return (
                <button
                  key={item.name}
                  className="vp-reset vp-nav"
                  onClick={() => {
                    if (gated) onLocked(group.upgradeTier ?? (group.tier === 'PRO' ? 'pro' : 'select'), item.upgradeFocus);
                    else if (item.screen) onNavigate(item.screen);
                  }}
                  aria-current={active ? 'page' : undefined}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '9px 12px',
                    borderRadius: radius.nav,
                    width: '100%',
                  }}
                >
                  <Dot color={active ? c.sky : rail.dotIdle} />
                  <span
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      fontSize: 14.5,
                      color: locked ? rail.locked : active ? c.sky : rail.primary,
                    }}
                  >
                    {item.name}
                  </span>
                  <span
                    style={{
                      fontFamily: mono,
                      fontSize: 11,
                      color: locked ? rail.metaLocked : rail.meta,
                    }}
                  >
                    {item.status ?? (gated ? (group.tier === 'PRO' && shaunAwaitingStreetLink ? 'waiting' : 'locked') : item.count)}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div
        style={{
          padding: '14px 22px 20px 22px',
          borderTop: `1px solid ${rail.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <Dot color={ctx.assistant.live ? c.sky : rail.dotIdle} size={7} pulse={ctx.assistant.live} />
          <span style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '0.06em', color: rail.duty }}>
            {ctx.assistant.live ? `ASSISTANT ON DUTY · ${branchLabel}` : `NOT CONNECTED · ${branchLabel}`}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span
            style={{
              width: 34,
              height: 34,
              flex: '0 0 34px',
              borderRadius: radius.pill,
              background: 'rgba(90,219,255,0.14)',
              border: '1px solid rgba(90,219,255,0.30)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12.5,
              color: c.sky,
            }}
          >
            {userInitials}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span
              style={{
                fontSize: 13.5,
                color: c.surface2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {ctx.tenant.name}
            </span>
            <span style={{ fontSize: 11.5, color: rail.userRole }}>
              {ctx.branch.name} branch · {ctx.tenant.tier} tier
            </span>
          </div>
        </div>
        {/* Back to the ModelProp console, where the agency sees every Prop product it has (M154). */}
        <a
          href={MODELPROP_CONSOLE_URL}
          style={{ fontSize: 13, color: rail.userRole, textDecoration: 'none' }}
        >
          ← All ModelProp products
        </a>
        <div style={{ marginTop: 2, paddingTop: 14, borderTop: `1px solid ${rail.divider}` }}>
          <ModelPropSignoff onDark />
        </div>
      </div>
    </aside>
  );
}
